import { Collapse, Grid, Stack, Typography } from "@mui/material";
import CopyableTypography from "components/Typography/CopyableTypography";
import { FieldDisplay } from "./shared/FieldComponents";

export function LepcDisplay({
  lepc,
  lepcLabel,
  isExpanded,
}: {
  lepc?: {
    name: string;
    id: string;
    email?: string | null;
    phone?: string | null;
    isMailed?: boolean | null;
    isEmailed?: boolean | null;
    needsT2sFile?: boolean | null;
    isStateSubmits?: boolean | null;
    city?: string | null;
    state?: string | null;
    mailingAddress?: string | null;
    mailingAddressLine2?: string | null;
    zip?: string | null;
    county?: string | null;
    additionalComments?: string | null;
  } | null;
  lepcLabel: string;
  isExpanded: boolean;
}) {
  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Stack direction="column" spacing={1}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", color: "text.secondary" }}
        >
          {lepcLabel}
        </Typography>
        <FieldDisplay
          label={`${lepcLabel} ID`}
          value={<CopyableTypography text={renderString(lepc?.id)} />}
        />
        <FieldDisplay
          label="Name"
          value={
            <CopyableTypography
              text={renderString(lepc?.name)}
              sx={{ fontWeight: "bold" }}
            />
          }
        />

        {/* Additional LEPC fields - always rendered but conditionally visible */}
        <Collapse in={isExpanded}>
          <Stack direction="column" spacing={1}>
            <FieldDisplay
              label="Email"
              value={<CopyableTypography text={renderString(lepc?.email)} />}
            />
            <FieldDisplay
              label="Phone"
              value={<CopyableTypography text={renderString(lepc?.phone)} />}
            />
            <FieldDisplay
              label="Is mailed"
              value={renderBoolean(lepc?.isMailed)}
            />
            <FieldDisplay
              label="Is emailed"
              value={renderBoolean(lepc?.isEmailed)}
            />
            <FieldDisplay
              label="Needs T2S file"
              value={renderBoolean(lepc?.needsT2sFile)}
            />
            <FieldDisplay
              label={`State submits to ${lepcLabel}`}
              value={renderBoolean(lepc?.isStateSubmits)}
            />
            <FieldDisplay
              label="City"
              value={<CopyableTypography text={renderString(lepc?.city)} />}
            />
            <FieldDisplay
              label="State"
              value={<CopyableTypography text={renderString(lepc?.state)} />}
            />
            <FieldDisplay
              label="Mailing Address"
              value={
                <CopyableTypography text={renderString(lepc?.mailingAddress)} />
              }
            />
            <FieldDisplay
              label="Address Line 2"
              value={
                <CopyableTypography
                  text={renderString(lepc?.mailingAddressLine2)}
                />
              }
            />
            <FieldDisplay
              label="Zip"
              value={<CopyableTypography text={renderString(lepc?.zip)} />}
            />
            <FieldDisplay
              label="County"
              value={<CopyableTypography text={renderString(lepc?.county)} />}
            />
            <FieldDisplay
              label="Additional comments"
              value={
                <CopyableTypography
                  text={renderString(lepc?.additionalComments)}
                />
              }
              alignItems="flex-start"
            />
          </Stack>
        </Collapse>
      </Stack>
    </Grid>
  );
}

export function FireDepartmentDisplay({
  fireDepartment,
  isExpanded,
}: {
  fireDepartment?: {
    id: string;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    isMailed?: boolean | null;
    isEmailed?: boolean | null;
    needsT2sFile?: boolean | null;
    isStateSubmits?: boolean | null;
    city?: string | null;
    state?: string | null;
    mailingAddress?: string | null;
    mailingAddressLine2?: string | null;
    zip?: string | null;
    county?: string | null;
    additionalComments?: string | null;
  } | null;
  isExpanded: boolean;
}) {
  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Stack direction="column" spacing={1}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", color: "text.secondary" }}
        >
          Fire Department
        </Typography>
        <FieldDisplay
          label="Fire Dept ID"
          value={<CopyableTypography text={renderString(fireDepartment?.id)} />}
        />
        <FieldDisplay
          label="Name"
          value={
            <CopyableTypography
              text={renderString(fireDepartment?.name)}
              sx={{ fontWeight: "bold" }}
            />
          }
        />

        {/* Additional Fire Department fields - always rendered but conditionally visible */}
        <Collapse in={isExpanded}>
          <Stack direction="column" spacing={1}>
            <FieldDisplay
              label="Email"
              value={
                <CopyableTypography
                  text={renderString(fireDepartment?.email)}
                />
              }
            />
            <FieldDisplay
              label="Phone"
              value={
                <CopyableTypography
                  text={renderString(fireDepartment?.phone)}
                />
              }
            />
            <FieldDisplay
              label="Is mailed"
              value={renderBoolean(fireDepartment?.isMailed)}
            />
            <FieldDisplay
              label="Is emailed"
              value={renderBoolean(fireDepartment?.isEmailed)}
            />
            <FieldDisplay
              label="Needs T2S file"
              value={renderBoolean(fireDepartment?.needsT2sFile)}
            />
            <FieldDisplay
              label="City"
              value={
                <CopyableTypography text={renderString(fireDepartment?.city)} />
              }
            />
            <FieldDisplay
              label="State"
              value={
                <CopyableTypography
                  text={renderString(fireDepartment?.state)}
                />
              }
            />
            <FieldDisplay
              label="Mailing Address"
              value={
                <CopyableTypography
                  text={renderString(fireDepartment?.mailingAddress)}
                />
              }
            />
            <FieldDisplay
              label="Address Line 2"
              value={
                <CopyableTypography
                  text={renderString(fireDepartment?.mailingAddressLine2)}
                />
              }
            />
            <FieldDisplay
              label="Zip"
              value={
                <CopyableTypography text={renderString(fireDepartment?.zip)} />
              }
            />
            <FieldDisplay
              label="County"
              value={
                <CopyableTypography
                  text={renderString(fireDepartment?.county)}
                />
              }
            />
            <FieldDisplay
              label="Additional comments"
              value={
                <CopyableTypography
                  text={renderString(fireDepartment?.additionalComments)}
                />
              }
              alignItems="flex-start"
            />
          </Stack>
        </Collapse>
      </Stack>
    </Grid>
  );
}

function renderString(v: string | undefined | null) {
  return v || "";
}

function renderBoolean(v: boolean | undefined | null) {
  return v ? "Yes" : "No";
}
